// client/src/components/forms/FormSubmissions.jsx
//
// Submissions table for one form (FormEditor's Submissions tab): newest
// first, 50 a page, one column per field, plus a CSV export of everything.
// Growth Phase 4, feature_key "forms".

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import { listSubmissions, submissionsCsvUrl } from '../../services/formsApi';

const PAGE = 50;

function cell(field, value) {
  if (field.field_type === 'checkbox') return value ? '✓' : '';
  return value == null ? '' : String(value);
}

export default function FormSubmissions({ tenant, formId }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await listSubmissions(tenant, formId, { limit: PAGE, offset: page * PAGE });
      setData(res);
    } catch {
      setError(t('forms.submissions.loadError', 'Could not load submissions.'));
    }
  }, [tenant, formId, page, t]);

  useEffect(() => { load(); }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <LoadingState />;
  const fields = (data.fields || []).filter((f) => f.field_type !== 'email');
  const total = data.total || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <section className="surface">
      <div className="fo-inline" style={{ justifyContent: 'space-between' }}>
        <h4>{t('forms.submissions.count', '{{count}} submissions', { count: total })}</h4>
        <a className="lm-btn secondary" href={submissionsCsvUrl(tenant, formId)} download>
          {t('forms.submissions.export', 'Export CSV')}
        </a>
      </div>
      {total === 0 ? (
        <p className="surface-muted perf-small">{t('forms.submissions.empty', 'No submissions yet. Share the form from the Share tab.')}</p>
      ) : (
        <>
          <div className="perf-table-wrap">
            <table className="perf-table">
              <thead>
                <tr>
                  <th>{t('forms.submissions.when', 'Submitted')}</th>
                  <th>{t('forms.submissions.email', 'Email')}</th>
                  {fields.map((f) => <th key={f.field_key}>{f.label}</th>)}
                  <th>{t('forms.submissions.optIn', 'Email opt-in')}</th>
                  <th>{t('forms.submissions.webhook', 'Webhook')}</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((s) => (
                  <tr key={s.id}>
                    <td>{s.createdon}</td>
                    <td>{s.email}</td>
                    {fields.map((f) => <td key={f.field_key}>{cell(f, s.data?.[f.field_key])}</td>)}
                    <td>{s.emailOptIn ? t('forms.yes', 'Yes') : t('forms.no', 'No')}</td>
                    <td title={s.webhookError || undefined}>{s.webhookStatus === 'none' ? '' : s.webhookStatus}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="fo-inline">
              <button type="button" className="lm-btn secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>{t('forms.prev', 'Newer')}</button>
              <span className="perf-small">{t('forms.pageOf', 'Page {{page}} of {{pages}}', { page: page + 1, pages })}</span>
              <button type="button" className="lm-btn secondary" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>{t('forms.next', 'Older')}</button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
